import axios from 'axios';
import { apiUrl } from '../../config';
import { isOpenAiConfigured, streamOpenAiAnswer } from './openaiApi';

function buildErrorContent(error) {
  if (error.response?.data?.response) {
    return error.response.data.response;
  }
  if (error.response) {
    return `Më vjen keq, pati një gabim (${error.response.status}). Ju lutem provoni përsëri ose kontrolloni që serveri të jetë i aktivizuar.`;
  }
  if (error.request) {
    return 'Më vjen keq, nuk mund të lidhem me serverin. Ju lutem sigurohuni që API-ja e chatbot-it është e aktivizuar dhe e arritshme nga kjo faqe.';
  }
  return `Më vjen keq, pati një gabim: ${error.message}`;
}

async function postOnce({ message, sessionId, conversationHistory }) {
  return axios.post(
    apiUrl('/api/chat/message'),
    { message, sessionId, conversationHistory },
    { headers: { 'Content-Type': 'application/json' }, timeout: 70000 }
  );
}

function parseSseEvents(buffer) {
  const events = [];
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    const line = part
      .split('\n')
      .find((l) => l.startsWith('data: '));
    if (!line) continue;
    try {
      events.push(JSON.parse(line.slice(6)));
    } catch (_) {
      /* ignore malformed chunk */
    }
  }
  return { events, rest };
}

/**
 * 1) Ask the DPSHTRR knowledge base.
 * 2) If keywords match → show that answer.
 * 3) If not → stream OpenAI immediately.
 * @returns {Promise<{ role: 'assistant', content: string, timestamp: Date, isError?: boolean, perf?: object }>}
 */
export async function postChatMessage({
  message,
  sessionId,
  conversationHistory,
  onChunk,
  onMeta
}) {
  const askOpenAi = async (fallbackReply) => {
    if (!isOpenAiConfigured() || typeof fetch !== 'function') return fallbackReply;
    try {
      // Clear any KB "no match" placeholder before OpenAI tokens arrive.
      onChunk?.('', '');
      return await streamOpenAiAnswer({ message, conversationHistory, onChunk });
    } catch (err) {
      if (err && err.streamedText) {
        return {
          role: 'assistant',
          content: err.streamedText,
          timestamp: new Date(),
          source: 'openai'
        };
      }
      return fallbackReply;
    }
  };

  if (typeof fetch === 'function') {
    try {
      const reply = await postChatMessageStream({
        message,
        sessionId,
        conversationHistory,
        onChunk,
        onMeta
      });
      if (reply.source === 'no_match') {
        return askOpenAi(reply);
      }
      return reply;
    } catch (err) {
      if (err && err.streamedText) {
        return {
          role: 'assistant',
          content: err.streamedText,
          timestamp: new Date()
        };
      }
      /* fall through to JSON */
    }
  }

  try {
    let response;
    try {
      response = await postOnce({ message, sessionId, conversationHistory });
    } catch (firstError) {
      if (firstError.request && !firstError.response) {
        await new Promise((r) => setTimeout(r, 700));
        response = await postOnce({ message, sessionId, conversationHistory });
      } else {
        throw firstError;
      }
    }

    if (response.data?.response) {
      const reply = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(response.data.timestamp),
        perf: response.data.perf,
        source: response.data.source
      };
      if (response.data.source === 'no_match') {
        return askOpenAi(reply);
      }
      return reply;
    }

    throw new Error(
      'API-ja nuk u gjet ose ktheu përgjigje të pavlefshme. Nëse përdorni demo-n lokale (port 5001), rindërtoni me npm run demo dhe sigurohuni që API-ja po punon në port 8000 (npm run server).'
    );
  } catch (error) {
    const serverFailed = {
      role: 'assistant',
      content: buildErrorContent(error),
      timestamp: new Date(),
      isError: true
    };
    return askOpenAi(serverFailed);
  }
}

async function postChatMessageStream({
  message,
  sessionId,
  conversationHistory,
  onChunk,
  onMeta
}) {
  const started = Date.now();
  let firstChunkMs = null;

  const resp = await fetch(apiUrl('/api/chat/message/stream'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, sessionId, conversationHistory })
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`stream failed: ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';
  let source = 'official_data';
  let perf;
  let timestamp = new Date();

  const fail = (message) => {
    const error = new Error(message);
    if (content) error.streamedText = content;
    throw error;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseSseEvents(buffer);
      buffer = rest;

      for (const evt of events) {
        if (evt.type === 'meta') {
          source = evt.source || source;
          onMeta?.(evt);
        } else if (evt.type === 'chunk' && evt.text) {
          if (firstChunkMs === null) firstChunkMs = Date.now() - started;
          content += evt.text;
          onChunk?.(evt.text, content);
        } else if (evt.type === 'done') {
          content = evt.response || content;
          source = evt.source || source;
          timestamp = evt.timestamp ? new Date(evt.timestamp) : timestamp;
          perf = evt.perf;
        } else if (evt.type === 'error') {
          fail(evt.message || 'stream error');
        }
      }
    }
  } catch (err) {
    if (content && err && !err.streamedText) err.streamedText = content;
    throw err;
  }

  if (!content) {
    throw new Error('empty stream response');
  }

  return {
    role: 'assistant',
    content,
    timestamp,
    source,
    perf,
    firstChunkMs
  };
}
